<template>
  <div 
    v-click-outside="close"
    class="bg-picker"
    :class="{ 'is-dragging-over': isDraggingOver }"
    @dragenter.prevent="onDragEnter"
    @dragover.prevent="onDragOver"
    @dragleave.prevent="onDragLeave"
    @drop.prevent="onDrop"
  >
    <button
      class="dbh-btn--outline dbh-btn text-sm"
      :class="{ 'is-open': isOpen }"
      :title="triggerTitle"
      :aria-expanded="isOpen.toString()"
      aria-haspopup="true"
      @click="toggle"
    >
      <svg 
        width="16" 
        height="16" 
        viewBox="0 0 16 16" 
        fill="none" 
        aria-hidden="true"
      >
        <rect 
          x="1" 
          y="1" 
          width="14" 
          height="14" 
          rx="2" 
          stroke="currentColor" 
          stroke-width="1.4" 
          fill="none"
        />
        <path 
          d="M1 10l4-4 3.5 3.5L12 5l3 5" 
          stroke="currentColor" 
          stroke-width="1.4" 
          stroke-linecap="round" 
          stroke-linejoin="round" 
          fill="none"
        />
        <circle 
          cx="5.5" 
          cy="6" 
          r="1.2" 
          fill="currentColor"
        />
      </svg>
      <span class="ml-1">Background</span>
    </button>

    <transition name="bg-picker__panel">
      <div
        v-if="isOpen"
        class="bg-picker__panel"
        role="menu"
        :style="panelPositionStyle"
      >
        <p class="bg-picker__panel-title">
          Background
        </p>

        <div
          v-for="layer in displayLayers"
          :key="layer.role"
          class="bg-picker__layer py-2"
        >
          <div class="bg-picker__layer-head">
            <div>
              <small>{{ layer.description }}</small>
            </div>
            <div class="d-flex align-items-center gap-1">
              <button
                v-if="hasDownloadableLayer(layer.role)"
                type="button"
                class="download-btn d-flex align-items-center gap-1"
                :disabled="downloadingRole === layer.role"
                :aria-label="`Download ${layer.label}`"
                @click.stop="downloadLayer(layer)"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  class="lucide lucide-download-icon lucide-download"
                >
                  <path d="M12 15V3" />
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <path d="m7 10 5 5 5-5" />
                </svg>
                Download
              </button>
              <span
                v-if="!layer.optional"
                class="bg-picker__status bg-picker__status--set"
              >
                Set
              </span>
              <span
                v-else
                class="bg-picker__status"
                :class="{ 'bg-picker__status--set': hasLayer(layer.role) }"
              >
                {{ hasLayer(layer.role) ? 'Set' : '(optional)' }}
              </span>
            </div>
          </div>

          <div class="bg-picker__layer-actions">
            <button
              class="bg-picker__option"
              role="menuitem"
              :disabled="layerBusy"
              @click="triggerUpload(layer.role)"
            >
              <i class="bi bi-upload" />
              Upload
            </button>
            <button
              class="bg-picker__option"
              role="menuitem"
              :disabled="layerBusy"
              @click="pickFromFiles(layer.role)"
            >
              <i class="bi bi-folder" />
              Files
            </button>
          </div>
        </div>

        <input
          ref="fileInput"
          type="file"
          accept="image/*"
          class="bg-picker__file-input"
          @change="onFileSelected"
        >
      </div>
    </transition>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { editorControlEventBroker, EVENTS } from '@frontend/group/modules/editor-control-popup/event-broker';
import API_MEDIA from "@frontend/services/api/media";
import {
  addAddField,
  DEFAULT_BACKGROUND_THEME_IMAGE_FIELD_PROPERTIES,
} from "@frontend/group/modules/additional-fields";
import { FIELD_TYPE_BACKGROND_THEME_IMAGE, FIELD_TYPE_PRODUCT_IMAGE } from '@frontend/constants/type-fields-of-template';
import { ShowPageScopeCollector } from '@frontend/group/modules/scope-collectors/show-page-collector';
import { cleanImageResourceUrl, downloadFileFromUrl } from '@frontend/utils';
import {
  DISPLAY_BUILDER_LAYER_META_KEY,
  DISPLAY_BUILDER_LAYER_ROLE_FOREGROUND,
  DISPLAY_BUILDER_LAYER_ROLE_LOGO,
  DISPLAY_BUILDER_LAYER_ROLE_REAR,
  createDisplayBuilderAssetKey,
  getAdditionalBackgroundIndex,
  normalizeDisplayBuilderLayerMeta,
} from '@frontend/components/display-builder/display-layer-metadata';

const getShowPageScope = () => ShowPageScopeCollector.get([
  'handleObjectMoving',
  'getChangesLayoutById',
  'replaceBackgroundImage',
  'resizeDisplayBuilderRearLayer',
  'updateLayoutChanges',
]);

export default {
  name: 'BackgroundPicker',
  directives: {
    clickOutside: {
      bind(el, binding) {
        el._clickOutsideHandler = (event) => {
          if (!el.contains(event.target)) binding.value(event);
        };
        document.addEventListener('mousedown', el._clickOutsideHandler);
      },
      unbind(el) {
        document.removeEventListener('mousedown', el._clickOutsideHandler);
      },
    },
  },
  model: {
    prop: 'value',
    event: 'change',
  },
  props: {
    value: {
      type: String,
      default: null,
    },
    placement: {
      type: String,
      default: 'bottom-right',
      validator: (v) => ['bottom-right', 'bottom-left', 'top-right', 'top-left'].includes(v),
    },
  },
  data() {
    return {
      isOpen: false,
      currentBackground: this.value || null,
      isDraggingOver: false,
      dragDepth: 0,
      pendingRole: DISPLAY_BUILDER_LAYER_ROLE_REAR,
      layerBusy: false,
      layerRevision: 0,
      downloadingRole: null,
    };
  },

  computed: {
    ...mapGetters('displayBuilder', ['isEditMode']),

    displayLayers() {
      return [
        {
          role: DISPLAY_BUILDER_LAYER_ROLE_REAR,
          label: 'Rear / background',
          description: 'Behind products',
        },
        {
          role: DISPLAY_BUILDER_LAYER_ROLE_FOREGROUND,
          label: 'Foreground / shelf front',
          description: 'Foreground image (optional)',
          optional: true,
        },
      ];
    },

    triggerTitle() {
      return this.currentBackground ? 'Change background' : 'Set background';
    },

    swatchStyle() {
      if (!this.currentBackground) return {};
      return {
        backgroundImage: `url(${this.currentBackground})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      };
    },

    panelPositionStyle() {
      const map = {
        'bottom-right': { top: 'calc(100% + 6px)', right: '0' },
        'bottom-left':  { top: 'calc(100% + 6px)', left: '0' },
        'top-right':    { bottom: 'calc(100% + 6px)', right: '0' },
        'top-left':     { bottom: 'calc(100% + 6px)', left: '0' },
      };
      return map[this.placement] || map['bottom-right'];
    },
  },

  watch: {
    value(v) {
      this.currentBackground = v;
    },
  },

  mounted() {
    window.addEventListener(
      'display-builder:background-layers-changed',
      this.onBackgroundLayersChanged
    );
  },

  beforeDestroy() {
    window.removeEventListener(
      'display-builder:background-layers-changed',
      this.onBackgroundLayersChanged
    );
  },

  methods: {
    onBackgroundLayersChanged() {
      this.layerRevision += 1;
    },

    displayLayerType(role) {
      return role === DISPLAY_BUILDER_LAYER_ROLE_FOREGROUND
        ? 'display-foreground'
        : 'display-rear';
    },

    getPreview() {
      return window.previews?.[0] || null;
    },

    getLayerMetadata() {
      const preview = this.getPreview();

      if (!preview) {
        return [];
      }

      const { getChangesLayoutById } = getShowPageScope();
      const changes = getChangesLayoutById?.(preview.id) || {};

      return normalizeDisplayBuilderLayerMeta(
        changes[DISPLAY_BUILDER_LAYER_META_KEY]
          ?? preview.data?.[DISPLAY_BUILDER_LAYER_META_KEY]
      );
    },

    getLayerObject(role) {
      // Make canvas-derived status update after asynchronous add/remove work.
      void this.layerRevision;
      const preview = this.getPreview();
      const metadata = this.getLayerMetadata();
      const logoIds = new Set(
        (window.displayBuilderState?.logos || [])
          .map((logo) => String(logo?.canvas_object_id || ''))
          .filter(Boolean)
      );
      const additionalObjects = preview?.canvas?.getObjects?.().filter((object) => {
        return getAdditionalBackgroundIndex(object) !== null;
      }) || [];
      const matched = additionalObjects.find((object) => {
        const index = getAdditionalBackgroundIndex(object);
        return metadata[index]?.role === role;
      });

      if (matched || role !== DISPLAY_BUILDER_LAYER_ROLE_REAR) {
        return matched || null;
      }

      // Backward compatibility: old layouts had no role metadata. Their
      // first non-logo additional background remains the rear layer.
      return additionalObjects.find((object) => {
        const index = getAdditionalBackgroundIndex(object);
        const metaRole = metadata[index]?.role;

        return !logoIds.has(String(object.id || ''))
          && metaRole !== DISPLAY_BUILDER_LAYER_ROLE_LOGO
          && metaRole !== DISPLAY_BUILDER_LAYER_ROLE_FOREGROUND;
      }) || null;
    },

    hasLayer(role) {
      return !!this.getLayerObject(role);
    },

    hasDownloadableLayer(role) {
      const object = this.getLayerObject(role);
      const index = getAdditionalBackgroundIndex(object);
      const preview = this.getPreview();

      if (!object || index === null || !preview) {
        return false;
      }

      const { getChangesLayoutById } = getShowPageScope();
      const changes = getChangesLayoutById?.(preview.id) || {};
      const images = changes.additional_background_images
        ?? preview.data?.additional_background_images
        ?? [];
      const source = images[index];

      if (!source) {
        return false;
      }

      try {
        const sourceUrl = new URL(String(source), window.location.origin);
        const imagePath = sourceUrl.searchParams.get('file') || sourceUrl.pathname;

        return imagePath !== DEFAULT_BACKGROUND_THEME_IMAGE_FIELD_PROPERTIES.additional_background_images;
      } catch (error) {
        return String(source) !== DEFAULT_BACKGROUND_THEME_IMAGE_FIELD_PROPERTIES.additional_background_images;
      }
    },

    getLayerDownloadUrl(object) {
      const source = object?.getSrc?.()
        || object?._element?.currentSrc
        || object?._element?.src
        || '';

      if (!source || String(source).startsWith('blob:') || String(source).startsWith('data:')) {
        return String(source || '');
      }

      // A download has to be the original: `variant=avif` handed the user AVIF bytes under the .png name built below.
      return cleanImageResourceUrl(String(source));
    },

    getLayerDownloadFilename(object, layer, url) {
      const objectFilename = object?._file?.name || object?.fileName;

      if (objectFilename) {
        return String(objectFilename).split(/[\\/]/).pop();
      }

      try {
        const parsedUrl = new URL(url, window.location.origin);
        const filePath = parsedUrl.searchParams.get('file') || parsedUrl.pathname;
        const filename = decodeURIComponent(filePath).split('/').filter(Boolean).pop();

        if (filename && filename.includes('.')) {
          return filename;
        }
      } catch (error) {
        // Fall through to a stable role-based filename.
      }

      return `${layer.role === DISPLAY_BUILDER_LAYER_ROLE_REAR ? 'background' : 'foreground'}.png`;
    },

    async downloadLayer(layer) {
      const object = this.getLayerObject(layer.role);
      const url = this.getLayerDownloadUrl(object);

      if (!object || !url) {
        toastr.error(`${layer.label} is not set`);
        return;
      }

      this.downloadingRole = layer.role;

      try {
        await downloadFileFromUrl(
          url,
          this.getLayerDownloadFilename(object, layer, url)
        );
      } finally {
        this.downloadingRole = null;
      }
    },

    assignLayerRole(object, role) {
      const preview = this.getPreview();
      const index = getAdditionalBackgroundIndex(object);

      if (!preview || index === null) {
        return;
      }

      const metadata = this.getLayerMetadata();
      const assetKey = metadata[index]?.assetKey
        || object.displayBuilderAssetKey
        || createDisplayBuilderAssetKey();

      metadata[index] = {
        ...(metadata[index] || {}),
        assetKey,
        role,
      };

      object.displayBuilderAssetKey = assetKey;
      object.set({
        displayBuilderType: this.displayLayerType(role),
        objectCaching: false,
      });
      object.displayBuilderType = this.displayLayerType(role);

      const { updateLayoutChanges } = getShowPageScope();
      updateLayoutChanges?.(preview.id, DISPLAY_BUILDER_LAYER_META_KEY, metadata);
      window.displayBuilderHelper?.setDisplayLayerMetadata?.(metadata);
      this.layerRevision += 1;
    },

    toggle() {
      this.isOpen = !this.isOpen;
    },

    close() {
      this.isOpen = false;
    },

    triggerUpload(role = DISPLAY_BUILDER_LAYER_ROLE_REAR) {
      this.pendingRole = role;
      this.$refs.fileInput.click();
    },

    pickFromFiles(role) {
      const existingObject = this.getLayerObject(role);

      this.pendingRole = role;

      if (existingObject) {
        existingObject.canvas?.setActiveObject?.(existingObject);
        editorControlEventBroker.fire(EVENTS.IMAGE_CHANGED, {
          action: 'selectBackground',
          value: 'Replace',
          background_id: existingObject.id,
        });
        this.close();
        return;
      }

      this.addAdditionalField('Background Theme Image', null, role);
    },

    async onFileSelected(event) {
      const file = event.target.files[0];

      await this.handleImageFile(file, this.pendingRole);
      event.target.value = '';
    },

    onDragEnter(event) {
      if (!this.hasImageFile(event)) return;
      this.dragDepth += 1;
      this.isDraggingOver = true;
    },

    onDragOver(event) {
      if (!this.hasImageFile(event)) return;
      event.dataTransfer.dropEffect = 'copy';
      this.isDraggingOver = true;
    },

    onDragLeave(event) {
      if (!this.dragDepth) return;
      this.dragDepth -= 1;
      this.isDraggingOver = this.dragDepth > 0;
    },

    async onDrop(event) {
      this.dragDepth = 0;
      this.isDraggingOver = false;

      const files = Array.from(event.dataTransfer?.files || []);
      const file = files
        .find((item) => this.isImageFile(item));

      if (files.length && !file) {
        toastr.error('Please drop an image file');
        return;
      }

      await this.handleImageFile(file, DISPLAY_BUILDER_LAYER_ROLE_REAR);
    },

    hasImageFile(event) {
      const items = Array.from(event.dataTransfer?.items || []);

      if (items.length) {
        return items.some((item) => item.kind === 'file' && item.type.startsWith('image/'));
      }

      return Array.from(event.dataTransfer?.files || []).some((file) => this.isImageFile(file));
    },

    isImageFile(file) {
      return file?.type?.startsWith('image/');
    },

    async handleImageFile(file, role = DISPLAY_BUILDER_LAYER_ROLE_REAR) {
      if (!file) {
        return;
      }

      if (!this.isImageFile(file)) {
        toastr.error('Please select an image file');
        return;
      }

      await this.addBackgroundImage(file, role);
    },

    addAdditionalField(type, file = null, role = DISPLAY_BUILDER_LAYER_ROLE_REAR) {
      const selectedInstance = window.previews[0];
      document.querySelector('.back-to-folder-link')?.click();

      const existingElementIds = new Set();
      const existingIds = new Set();
      
      const currentObjects = selectedInstance.canvas.getObjects();
      currentObjects.forEach((obj, index) => {
        if (obj.element_id) existingElementIds.add(obj.element_id);
        if (obj.id) existingIds.add(obj.id);
      });

      selectedInstance.canvas.once('object:added', (e) => {
        const file = e.target?._file || null;
        setTimeout(() => {
          const updatedObjects = selectedInstance.canvas.getObjects();
          
          let newObject = null;

          updatedObjects.forEach((obj, index) => {
            if (obj.element_id && !existingElementIds.has(obj.element_id)) {
              newObject = obj;
            }
            else if (obj.id && !existingIds.has(obj.id)) {
              if (!newObject) newObject = obj;
            }
          });

          if (!newObject && updatedObjects.length > currentObjects.length) {
            newObject = updatedObjects[updatedObjects.length - 1];
          }

          if (newObject) {
            this.assignLayerRole(newObject, role);
            newObject.set({
              displayBuilderType: this.displayLayerType(role),
              objectCaching: false,
            });
            newObject.displayBuilderType = this.displayLayerType(role);
            selectedInstance.canvas.setActiveObject(newObject);
            selectedInstance.canvas.renderAll();

            editorControlEventBroker.fire(EVENTS.IMAGE_CHANGED, {
              action: 'selectBackground',
              background_id: newObject.id,
              file,
            });

            this.applyBackgroundEditState(newObject);
          }
        }, 50);
      });

      selectedInstance.canvas.fire('add:additional-field', {
        instancesIds: [selectedInstance.id],
        type,
        options: {
          file,
          deleteWhenUserClosesImageSelectionModal: true,
          skipAutoResize: role === DISPLAY_BUILDER_LAYER_ROLE_FOREGROUND,
        }
      })
    },
    async addBackgroundImage(file, role = DISPLAY_BUILDER_LAYER_ROLE_REAR) {
      this.close();
      const { id: templateId, customer_id, preview_id } = window.template_data;
      const canvas = window.previews[0]?.canvas;

      if (templateId == null || customer_id == null || preview_id == null) {
        toastr.error('Template data is incomplete');
        return;
      }

      if (!file) {
        toastr.error('No file selected');
        return;
      }

      $('.loadingOverlay').fadeIn();
      $('.loadingOverlay-text').html('Adding Background Image...');
      this.layerBusy = true;
      try {
        const formData = new FormData();

        formData.append('filename_1', file.name);
        formData.append('customer_id', customer_id);
        formData.append('template_id', templateId);
        formData.append('file_1', file);
        formData.append('remove_background', 0);

        const uploadResponse = await API_MEDIA.uploadBackgroundImage(formData);

        if (!uploadResponse?.data?.[0]?.path) {
          $('.loadingOverlay').fadeOut();
          console.error('Invalid upload response:', uploadResponse);
          toastr.error('Upload succeeded but returned invalid response');
          return;
        }

        const backgroundUrl = `/share?file=${uploadResponse.data[0].path}`;
        const existingObject = this.getLayerObject(role);

        if (existingObject) {
          const { handleObjectMoving, replaceBackgroundImage } = getShowPageScope();
          const replacement = await replaceBackgroundImage(
            backgroundUrl,
            existingObject.id,
            preview_id,
            false
          );

          this.assignLayerRole(replacement, role);
          this.applyBackgroundEditState(replacement);
          handleObjectMoving?.({ target: replacement });
          window.displayBuilderHelper?.orderDisplayBuilderLayers?.();
          canvas.requestRenderAll();

          if (role === DISPLAY_BUILDER_LAYER_ROLE_REAR) {
            this.currentBackground = backgroundUrl;
            this.$emit('change', backgroundUrl);
          }

          return;
        }

        const beforeIds = canvas.getObjects()
          .map((object) => String(object.id))
          .filter(Boolean);

        const newBackgroundObjectPromise = this.waitForNewCanvasObject(
          canvas,
          beforeIds,
          (object) => {
            return object.fieldType === FIELD_TYPE_BACKGROND_THEME_IMAGE;
          }
        );

        await addAddField(
          FIELD_TYPE_BACKGROND_THEME_IMAGE,
          preview_id,
          {
            additional_background_images: backgroundUrl,
            skipAutoResize: role === DISPLAY_BUILDER_LAYER_ROLE_FOREGROUND,
          }
        );

        const newBackgroundObject = await newBackgroundObjectPromise;
        if (newBackgroundObject) {
          this.assignLayerRole(newBackgroundObject, role);
          newBackgroundObject.set({
            displayBuilderType: this.displayLayerType(role),
            objectCaching: false,
          });
          newBackgroundObject.displayBuilderType = this.displayLayerType(role);

          const {
            handleObjectMoving,
            resizeDisplayBuilderRearLayer,
          } = getShowPageScope();

          if (role === DISPLAY_BUILDER_LAYER_ROLE_REAR) {
            resizeDisplayBuilderRearLayer?.(newBackgroundObject);
          } else {
            // Foreground artwork keeps the dimensions produced by the normal
            // image loader; only persist its untouched position and scale.
            newBackgroundObject.setCoords();
            handleObjectMoving?.({ target: newBackgroundObject });
          }

          this.applyBackgroundEditState(newBackgroundObject);

          window.displayBuilderHelper?.orderDisplayBuilderLayers?.();
          canvas.requestRenderAll();
        }

        if (role === DISPLAY_BUILDER_LAYER_ROLE_REAR) {
          this.currentBackground = backgroundUrl;
          this.$emit('change', backgroundUrl);
        }
        $('.loadingOverlay').fadeOut();
      } catch (err) {
        $('.loadingOverlay').fadeOut();
        console.error('addBackgroundImage error:', err);
        toastr.error(err.message || 'Unexpected error while uploading background image');
      } finally {
        this.layerBusy = false;
        $('.loadingOverlay').fadeOut();
        this.layerRevision += 1;
      }
    },
    waitForNewCanvasObject(canvas, beforeIds, matcher) {
      return new Promise((resolve) => {
        const onObjectAdded = (event) => {
          const object = event?.target;

          if (!object || !object.id) {
            return;
          }

          if (beforeIds.includes(String(object.id))) {
            return;
          }

          if (matcher && !matcher(object)) {
            return;
          }

          canvas.off('object:added', onObjectAdded);
          resolve(object);
        };

        canvas.on('object:added', onObjectAdded);
      });
    },
    applyBackgroundEditState(backgroundObject) {
      if (!backgroundObject) {
        return;
      }

      const isLocked = !this.isEditMode;
      const canvas = backgroundObject.canvas;

      backgroundObject.set({
        locked: isLocked,
        selectable: !isLocked,
        evented: !isLocked,
        lockMovementX: isLocked,
        lockMovementY: isLocked,
        lockScalingX: isLocked,
        lockScalingY: isLocked,
        lockRotation: isLocked,
        hasControls: !isLocked,
        hasBorders: !isLocked,
        hoverCursor: isLocked ? 'default' : 'move',
        moveCursor: 'move',
      });

      backgroundObject.setCoords();
      canvas?.fire('locked:changed', backgroundObject);

      if (isLocked && canvas?.getActiveObjects?.().includes(backgroundObject)) {
        canvas.discardActiveObject();
      }

      canvas?.requestRenderAll();
    },
  },
};
</script>

<style scoped>
.bg-picker {
  --bg-picker-teal:      #2eb89a;
  --bg-picker-teal-dim:  rgba(46, 184, 154, 0.12);
  --bg-picker-border:    #e4e7eb;
  --bg-picker-bg:        #ffffff;
  --bg-picker-surface:   #f8f9fb;
  --bg-picker-hover:     #f2f4f7;
  --bg-picker-text:      #1a1d23;
  --bg-picker-muted:     #6b7280;
  --bg-picker-light:     #9ca3af;
  --bg-picker-danger:    #ef4444;
  --bg-picker-danger-bg: #fef2f2;
  --bg-picker-radius:    8px;
  --bg-picker-radius-sm: 5px;
  --bg-picker-shadow:    0 6px 20px rgba(0,0,0,.11), 0 1px 4px rgba(0,0,0,.07);
  --bg-picker-font:      'DM Sans', system-ui, sans-serif;
  --border:      #e4e7eb;
  --bg-white:    #ffffff;
  --text-base:   #1a1d23;
}

.bg-picker {
  position: relative;
  display: inline-flex;
  font-family: var(--bg-picker-font);
}

.bg-picker.is-dragging-over .bg-picker__trigger,
.bg-picker.is-dragging-over .bg-picker__panel {
  border-color: var(--bg-picker-teal);
  box-shadow: 0 0 0 3px rgba(46, 184, 154, 0.15);
}

.bg-picker__trigger {
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--bg-picker-bg);
  border-radius: var(--bg-picker-radius-sm);
  color: var(--bg-picker-muted);
  cursor: pointer;
  padding: 0;
  transition: background 0.14s, border-color 0.14s, color 0.14s, box-shadow 0.14s;
  flex-shrink: 0;
  overflow: hidden;
  font-size: .875rem;
  line-height: 1.25rem;
  background: var(--bg-white);
  border-color: var(--border);
  color: var(--text-base);
}

.bg-picker__trigger:hover {
  background: var(--bg-picker-surface);
  border-color: #c9cdd4;
  color: var(--bg-picker-text);
}

.bg-picker__trigger.is-open {
  border-color: var(--bg-picker-teal);
  color: var(--bg-picker-teal);
  background: var(--bg-picker-teal-dim);
  box-shadow: 0 0 0 3px rgba(46, 184, 154, 0.15);
}

.bg-picker__swatch {
  width: 100%;
  height: 100%;
  display: block;
  border-radius: calc(var(--bg-picker-radius-sm) - 1px);
}

.bg-picker__panel {
  position: absolute;
  z-index: 1050;
  width: 16.5rem;
  background: var(--bg-picker-bg);
  border: 1px solid var(--bg-picker-border);
  border-radius: var(--bg-picker-radius);
  box-shadow: var(--bg-picker-shadow);
  padding: 10px;
  outline: none;
}

.bg-picker__panel-title {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--bg-picker-light);
  margin: 0;
  padding: 0;
}

.bg-picker__layer {
  padding-bottom: 10px;
  border-top: 1px solid var(--bg-picker-border);
}

.bg-picker__layer:first-of-type {
  border-top: 0;
}

.bg-picker__layer-head,
.bg-picker__layer-actions {
  display: flex;
  align-items: center;
}

.bg-picker__layer-head {
  justify-content: space-between;
  gap: 8px;
  font-size: 12px;
}

.bg-picker__layer-head small {
  display: block;
  margin-top: 2px;
  color: var(--bg-picker-muted);
  font-weight: 400;
}

.bg-picker__layer .download-btn {
  opacity: 0;
  visibility: hidden;
  pointer-events: none;
  padding: 0;
  border: 0;
  background: transparent;
  font-size: 10px;
  color: #64748b;
  line-height: 1;
  transition: opacity .15s ease, visibility .15s ease;
  cursor: pointer;
}

.bg-picker__layer:hover .download-btn,
.bg-picker__layer:focus-within .download-btn {
  opacity: 1;
  visibility: visible;
  pointer-events: auto;
}

.bg-picker__layer .download-btn svg {
  width: 12px;
  height: 12px;
}

.bg-picker__layer .download-btn:hover {
  color: #1a1d23;
}

.bg-picker__layer .download-btn:disabled {
  cursor: wait;
}

.bg-picker__status {
  border-radius: 999px;
  padding: 2px 7px;
  color: #64748bb3;
  font-size: 10px;
}

.bg-picker__status--set {
  color: #137f69;
  background: var(--bg-picker-teal-dim);
}

.bg-picker__layer-actions {
  gap: 6px;
  margin-top: 8px;
}

.bg-picker__layer-actions .bg-picker__option {
  justify-content: center;
  gap: 5px;
  margin-top: 0;
  padding: 7px 8px;
  font-size: 11px;
}

.bg-picker__option:disabled {
  opacity: .5;
  cursor: not-allowed;
}

.bg-picker__option {
  display: flex;
  align-items: center;
  gap: 9px;
  width: 100%;
  padding: 8px 12px;
  font-family: var(--bg-picker-font);
  font-size: 13px;
  font-weight: 500;
  color: var(--bg-picker-text);
  background: transparent;
  border: 1px solid #e4e7eb;
  border-radius: 8px;
  text-align: left;
  cursor: pointer;
  transition: background 0.12s, color 0.12s;
  line-height: 1.3;
  margin-top: 10px;
}

.bg-picker__option:hover {
  background: var(--bg-picker-hover);
}

.bg-picker__option--danger {
  color: var(--bg-picker-danger);
}

.bg-picker__option--danger:hover {
  background: var(--bg-picker-danger-bg);
}

.bg-picker__option-icon {
  display: flex;
  align-items: center;
  color: var(--bg-picker-muted);
  flex-shrink: 0;
}

.bg-picker__option--danger .bg-picker__option-icon {
  color: var(--bg-picker-danger);
}

.bg-picker__file-input {
  position: absolute;
  width: 1px;
  height: 1px;
  opacity: 0;
  pointer-events: none;
}

.bg-picker__panel-enter-active,
.bg-picker__panel-leave-active {
  transition: opacity 0.16s ease, transform 0.16s ease;
}

.bg-picker__panel-enter,
.bg-picker__panel-leave-to {
  opacity: 0;
  transform: translateY(-5px) scale(0.97);
}
.dbh-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 500;
  border-radius: var(--radius-sm);
  border: 1px solid transparent;
  padding: 0.5rem 1rem;
  cursor: pointer;
  white-space: nowrap;
  transition: background .14s, border-color .14s, color .14s, box-shadow .14s;
  line-height: 1;
}
.dbh-btn--outline {
  background: var(--bg-white);
  border-color: var(--border);
  color: var(--text-base);
}
.dbh-btn--outline:hover {
  background: var(--bg-surface);
  border-color: #d0d5dd;
}
</style>
